import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { NEUTRAL, BACK, NEXT, SLIDE_UP, COLUMN, NOTIFY } from 'components/constants'
import { LABEL_PRINT_DENSITY } from 'components/printableLabels/constants'
import { PRINTER_TYPES, PRINTABLE_TYPES } from 'constants/settings'
import { LiquidRenderer } from 'helpers/liquid/liquidRenderer.mjs'

import useSyncProducts from 'hooks/useSyncProducts'

import convertSizeUnit from 'components/printableLabels/helpers/convertSizeUnit'

import Banner from 'components/shared/Banner'
import Topbar from 'components/navigation/Topbar'
import View from 'components/shared/View'
import ProductSelection from 'components/printableLabels/ProductSelection'
import PrintDetails from 'components/printableLabels/PrintDetails'
import useHandlePrinting from 'hooks/useHandlePrinting'

const PrintableLabels = ({ initialAnimation }) => {
  const { products } = useSyncProducts({ poll: false })

  const outlets = useSelector((state) => state.outlets)

  const [animation, setAnimation] = useState(initialAnimation)
  const [selectedProducts, setSelectedProducts] = useState([])
  const [isLoading, setLoading] = useState(products.length === 0)
  const [isPreparingPreview, setIsPreparingPreview] = useState(false)
  const [showPrintDetails, setShowPrintDetails] = useState(false)
  const [selectedLabelTemplate, setSelectedLabelTemplate] = useState(null)
  const [previewBlob, setPreviewBlob] = useState(null)
  const [printQuantity, setPrintQuantity] = useState(1)
  const [keyword, setKeyword] = useState('')

  const {
    deviceError,
    printingTemplates,
    deviceAvailable,
    print,
  } = useHandlePrinting(PRINTER_TYPES.LABEL, PRINTABLE_TYPES.PRODUCT)

  useEffect(() => {
    if (products.length > 0 && printingTemplates.length > 0) {
      setLoading(false)
    }

    // After label templates got loaded set a selecte/setd label right away
    // to avoid an empty printing details screen
    if (printingTemplates.length > 0) {
      setSelectedLabelTemplate(printingTemplates[0])
    }
  }, [products, printingTemplates])

  async function prepareLabels(template, generatePreview = false) {
    if (generatePreview) setIsPreparingPreview(true)

    const liquidRenderer = new LiquidRenderer("en-AU", "AUD");
    liquidRenderer
      .render(template.label_template, { product: selectedProducts[0] })
      .then(rendered => generatePreviewLabel(selectedLabelTemplate, rendered));
  }

  // For now we are calling labelary's api.
  function generatePreviewLabel(template, processed_zpl) {
    const options = {
      encoding: null,
      method: 'POST',
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: JSON.stringify(processed_zpl),
    }

    const width = convertSizeUnit(template.width, template.size_unit)
    const height = convertSizeUnit(template.height, template.size_unit)
    const dpi = LABEL_PRINT_DENSITY[template.dpi]
    const size = `${width}x${height}`
    let url = `http://api.labelary.com/v1/printers/${dpi}/labels/${size}/0/`

    fetch(url, options)
      .then(response => {
        if (response.status == 200) {
          return response.blob()
        }
      })
      .then(blob => {
        setIsPreparingPreview(false)
        setPreviewBlob(blob)
      })
  }

  function onPrint() {
    print(selectedProducts, selectedLabelTemplate, printQuantity)
  }

  return (
    <View className="expand-y expand-x" theme={NEUTRAL} shade={1} animation={animation}>
      <Topbar
        className='border-bottom'
        shade={0}
        theme={NEUTRAL}
      />
      {showPrintDetails === false && (
        <ProductSelection
          cartItems={[]}
          products={products}
          outlets={outlets}
          loading={isLoading}
          currentOutlet={outlets.find((outlet) => !!outlet.current)}
          onAddToCart={() => null}
          limitedColumns={true}
          layoutSelectable={false}
          preSelectedProducts={selectedProducts}
          batchSelectable={true}
          batchSelectedAction={() => {
            setShowPrintDetails(true)
            setAnimation(NEXT)
          }}
          defaultKeyword={keyword}
          onKeywordChange={(keyword) => setKeyword(keyword)}
          onSelectedProductsChange={(selectedProducts) => setSelectedProducts(selectedProducts)}
        />
      )}
      {showPrintDetails === true && (
        <PrintDetails
          printError={deviceError}
          printerAvailable={deviceAvailable}
          animation={animation}
          labelTemplates={printingTemplates}
          previewBlob={previewBlob}
          prepareLabels={prepareLabels}
          selectedProducts={selectedProducts}
          selectedLabelTemplate={selectedLabelTemplate}
          setSelectedLabelTemplate={setSelectedLabelTemplate}
          isPreparingPreview={isPreparingPreview}
          printQuantity={printQuantity}
          onQuantityChange={(quantity) => {
            setPrintQuantity(quantity)
          }}
          onPrint={onPrint}
          onBack={() => {
            setAnimation(BACK)
            setShowPrintDetails(false)
            setPreviewBlob(null)
          }}
        />
      )}

      {(deviceError !== null || deviceAvailable === false) && (
        <Banner
          direction={COLUMN}
          animation={SLIDE_UP}
          theme={NOTIFY}
          shade={3}
          message={deviceError}
        />
      )}
    </View>
  )
}

export default PrintableLabels